/**
 * Централизованный HTTP-клиент: Bearer access, `credentials: 'include'` для httpOnly refresh cookie, refresh при 401 (кроме `/auth/*`).
 * Не используйте сырой `fetch` для API — только `api.*`.
 *
 * @example
 * ```ts
 * const data = await api.get(`/projects/${projectId}`);
 * await api.post('/auth/login', { email, password });
 * const blob = await api.getBlob(`/projects/${id}/export`);
 * ```
 */
import { RT_CLIENT_HEADER, type PaywallReason, type PlanId } from '@sloy/shared';
import { useAuthStore, type User } from '@/stores/authStore';
import { queryClient } from '@/lib/queryClient';
import { realtimeClientId } from '@/features/realtime/realtimeClientId';
import { usePaywallStore } from '@/stores/paywallStore';

/** Ошибка API с опциональным `code` из тела ответа (например `EMAIL_NOT_VERIFIED`). */
export class ApiRequestError extends Error {
  readonly code?: string;

  constructor(message: string, code?: string) {
    super(message);
    this.name = 'ApiRequestError';
    this.code = code;
  }
}

/**
 * Серверный отказ по тарифу (`billing-gate.ts`) открывает paywall-модалку прямо
 * здесь: UI-гейты — косметика, а этот перехват гарантирует, что ЛЮБОЙ доступ к
 * платной фиче в обход кнопок упрётся в то же окно с тарифами.
 */
function maybeOpenPaywall(err: unknown): void {
  const e = err as {
    code?: string;
    feature?: string;
    requiredPlan?: string;
    workspaceId?: string;
  };
  if (e.code === 'plan_limit' && typeof e.feature === 'string') {
    usePaywallStore
      .getState()
      .open(
        e.feature as PaywallReason,
        e.requiredPlan as PlanId | undefined,
        typeof e.workspaceId === 'string' ? e.workspaceId : null,
      );
  }
}

export interface MapStyleRecord {
  id: string;
  projectId: string;
  name: string;
  patch: unknown;
  createdAt: string;
}

/** Стиль из списка «все проекты пользователя» (импорт из другого проекта). */
export interface UserMapStyleRecord extends MapStyleRecord {
  projectName: string;
}

/**
 * Базовый URL API для fetch.
 * - По умолчанию `/api` — тот же origin (dev: Vite proxy, prod: nginx и т.п.).
 * - Если задан `VITE_API_BASE` (например `https://api.example.com/api`) — запросы идут туда.
 */
function resolveApiBase(): string {
  const raw = import.meta.env.VITE_API_BASE;
  if (typeof raw === 'string' && raw.trim().length > 0) {
    return raw.trim().replace(/\/$/, '');
  }
  return '/api';
}

export const API_BASE = resolveApiBase();

/**
 * Эндпоинты, для которых НЕ нужно перехватывать 401
 * (логин/регистрация сами возвращают 401 при неверных данных —
 *  это не повод разлогинивать пользователя и перезагружать страницу).
 */
const AUTH_ENDPOINTS = [
  '/auth/login',
  '/auth/register',
  '/auth/verify-email',
  '/auth/resend-verification',
  '/auth/refresh',
  '/auth/logout',
  '/auth/forgot-password',
  '/auth/reset-password',
];

function isAuthEndpoint(path: string): boolean {
  return AUTH_ENDPOINTS.some((ep) => path.startsWith(ep));
}

/** Флаг, предотвращающий параллельные refresh-запросы */
let refreshPromise: Promise<boolean> | null = null;

interface RefreshResponseBody {
  tokens: { accessToken: string };
  user?: User;
}

function applyRefreshResponseBody(data: RefreshResponseBody): void {
  const { setAuth, updateTokens } = useAuthStore.getState();
  if (data.user) {
    setAuth(data.user, data.tokens);
  } else {
    updateTokens(data.tokens);
  }
}

/**
 * Обмен httpOnly refresh cookie на access (и пользователя — ответ как у login).
 * Refresh по-прежнему не попадает в JS.
 */
async function fetchRefreshFromCookie(): Promise<RefreshResponseBody | null> {
  try {
    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });

    if (!res.ok) {
      return null;
    }

    return (await res.json()) as RefreshResponseBody;
  } catch {
    return null;
  }
}

async function executeRefreshTokenExchange(): Promise<boolean> {
  const { logout } = useAuthStore.getState();

  const data = await fetchRefreshFromCookie();
  if (!data) {
    logout();
    return false;
  }

  applyRefreshResponseBody(data);
  return true;
}

/**
 * Обновление access через httpOnly refresh cookie. Один общий in-flight запрос
 * на всех вызывающих (bootstrap при старте и retry после 401).
 */
async function tryRefreshToken(): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = executeRefreshTokenExchange().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

/** Восстановление сессии после гидрации persist (новая вкладка / перезагрузка). */
export async function tryRestoreSessionFromRefreshCookie(): Promise<boolean> {
  return tryRefreshToken();
}

/**
 * Выполняет HTTP-запрос с автоматическим retry при 401.
 * При провале refresh разлогинивает и перенаправляет на /login.
 * @param performFetch - функция, принимающая headers и возвращающая Response
 * @param path - путь запроса (для проверки isAuthEndpoint)
 */
async function withAuthRetry(
  performFetch: (headers: Record<string, string>) => Promise<Response>,
  path: string,
): Promise<Response> {
  const token = useAuthStore.getState().accessToken;
  // Id вкладки: сервер эхом кладёт его в realtime-события, автор их пропускает.
  const headers: Record<string, string> = { [RT_CLIENT_HEADER]: realtimeClientId };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await performFetch(headers);

  if (res.status !== 401) {
    return res;
  }

  if (isAuthEndpoint(path)) {
    return res;
  }

  const refreshed = await tryRefreshToken();

  if (refreshed) {
    const newToken = useAuthStore.getState().accessToken;
    const retryHeaders = { ...headers, Authorization: `Bearer ${newToken}` };
    return performFetch(retryHeaders);
  }

  queryClient.clear();
  window.location.href = '/login';
  throw new Error('Сессия истекла');
}

async function fetchApi(path: string, options: RequestInit = {}) {
  const res = await withAuthRetry((authHeaders) => {
    // `RequestInit.headers` — это `HeadersInit`: объект, `Headers` или массив пар.
    // Спред работает только для объекта: `Headers` растекается в `{}` (нет собственных
    // перечисляемых свойств), массив пар — в `{0: [...], 1: [...]}`. То есть заголовки
    // вызывающего молча терялись бы. `new Headers()` нормализует все три формы.
    const headers = new Headers(options.headers);
    for (const [key, value] of Object.entries(authHeaders)) {
      headers.set(key, value);
    }

    // Ставим Content-Type только когда есть тело и оно не FormData.
    // Без этой проверки DELETE-запросы отправляются с Content-Type: application/json
    // при пустом теле, что приводит к ошибке парсинга на Fastify (400 Bad Request).
    if (options.body !== undefined && !(options.body instanceof FormData)) {
      headers.set('Content-Type', 'application/json');
    }

    return fetch(`${API_BASE}${path}`, { ...options, credentials: 'include', headers });
  }, path);

  if (isAuthEndpoint(path) && res.status === 401) {
    const err = await res.json().catch(() => ({ error: 'Ошибка авторизации' }));
    throw new ApiRequestError(
      (err as { error?: string }).error || 'Ошибка авторизации',
      (err as { code?: string }).code,
    );
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Ошибка запроса' }));
    maybeOpenPaywall(err);
    throw new ApiRequestError(
      (err as { error?: string }).error || 'Ошибка запроса',
      (err as { code?: string }).code,
    );
  }

  if (res.status === 204) {
    return undefined;
  }

  const text = await res.text();
  if (!text) {
    return undefined;
  }
  return JSON.parse(text);
}

async function fetchApiBlob(path: string): Promise<Blob> {
  const res = await withAuthRetry(
    (authHeaders) =>
      fetch(`${API_BASE}${path}`, { credentials: 'include', headers: authHeaders }),
    path,
  );

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Ошибка запроса' }));
    throw new ApiRequestError(
      (err as { error?: string }).error || 'Ошибка запроса',
      (err as { code?: string }).code,
    );
  }

  return res.blob();
}

export const api = {
  get: (path: string) => fetchApi(path),

  getBlob: (path: string) => fetchApiBlob(path),

  post: (path: string, body?: unknown) =>
    fetchApi(path, {
      method: 'POST',
      body: body instanceof FormData ? body : JSON.stringify(body),
    }),

  put: (path: string, body?: unknown) =>
    fetchApi(path, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  patch: (path: string, body?: unknown) =>
    fetchApi(path, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  delete: (path: string) => fetchApi(path, { method: 'DELETE' }),

  /**
   * DELETE with keepalive: true — use for cleanup in beforeunload/unload
   * handlers where the browser may cancel regular fetch requests.
   * Fire-and-forget: returns void, errors are silently ignored.
   */
  deleteKeepalive: (path: string) => {
    const token = useAuthStore.getState().accessToken;
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    try {
      void fetch(`${API_BASE}${path}`, { method: 'DELETE', keepalive: true, credentials: 'include', headers });
    } catch {
      // keepalive fetch may throw in some environments; silently ignore
    }
  },

  /**
   * POST FormData with `keepalive: true` — рассчитан на fire-and-forget отправку
   * в обработчиках `visibilitychange` / unmount (напр. превью проекта). Без
   * refresh-retry, Bearer берём из текущего `authStore`.
   */
  postFormKeepalive: (path: string, form: FormData) => {
    const token = useAuthStore.getState().accessToken;
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    try {
      void fetch(`${API_BASE}${path}`, {
        method: 'POST',
        body: form,
        keepalive: true,
        credentials: 'include',
        headers,
      });
    } catch {
      // keepalive fetch may throw in some environments; silently ignore
    }
  },

  /**
   * Generates a custom map style from a natural language description.
   * Returns a style patch and suggested theme name.
   */
  generateMapStyle: (description: string) =>
    fetchApi('/ai/generate-map-style', {
      method: 'POST',
      body: JSON.stringify({ description }),
    }),

  getProjectMapStyles: (projectId: string) =>
    fetchApi(`/projects/${projectId}/map-styles`) as Promise<MapStyleRecord[]>,

  createProjectMapStyle: (projectId: string, name: string, patch: unknown) =>
    fetchApi(`/projects/${projectId}/map-styles`, {
      method: 'POST',
      body: JSON.stringify({ name, patch }),
    }) as Promise<MapStyleRecord>,

  deleteProjectMapStyle: (projectId: string, styleId: string) =>
    fetchApi(`/projects/${projectId}/map-styles/${styleId}`, { method: 'DELETE' }),

  updateProjectMapStyle: (
    projectId: string,
    styleId: string,
    body: { name?: string; patch?: unknown },
  ) =>
    fetchApi(`/projects/${projectId}/map-styles/${styleId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }) as Promise<MapStyleRecord>,

  getUserMapStyles: () => fetchApi('/user/map-styles') as Promise<UserMapStyleRecord[]>,

  importProjectMapStyle: (projectId: string, sourceStyleId: string) =>
    fetchApi(`/projects/${projectId}/map-styles/import`, {
      method: 'POST',
      body: JSON.stringify({ sourceStyleId }),
    }) as Promise<MapStyleRecord>,
};
